import React from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import EngageHomepage from "./Engage/EngageHomepage";
import RankPredictorHomePage from "./RankPredictor/RankPredictorHomePage";
import EngageProfile from "./Engage/EngageProfile/EngageProfile";
import XpPointsInfoModal from "./Engage/EngageProfile/XpPointsInfoModal";
import DailyGoalModal from "./Engage/EngageProfile/DailyGoalModal";
import EngageHomepageHeader from "./Engage/EngageHomepageHeader";
import { url_6, url_8, url_293, url_294 } from "../../custom_utilities/api_services";
import { toastifyMessage } from "../../custom_utilities/toastify_config";
import authActions from "../../redux/auth/action";
import userPreferenceActions from "../../redux/user_preference/action";
import "./Engage/EngageHomepage.css";
import UserAnalyticsContentComponent from "../../containers/logged_user_components/user_analytics/user_analytics_content_component/user_analytics_content_component";
import UserBookmarksContentComponent from "../../containers/logged_user_components/user_bookmarks/user_bookmarks_content_component/user_bookmarks_content_component";
import NotificationListPanel from "../notifications/NotificationListPanel";
import MySpaceReferAndEarn from "./ReferAndEarn/MySpaceReferAndEarn";
import WalletContentComponent from "../../containers/logged_user_components/Wallet/WalletComponent/WalletContentComponent/WalletContentComponent";
import ScheduleClass from "../../containers/logged_user_components/user_dashboard/user_dashboard_content_component/dashboard_utlility_components/schedule_class/schedule_class";
import HomeWorkContent from "../../containers/logged_user_components/user_exercise_test/user_homework/HomeWorkContent";

export default function MySpace(props) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user);
  const userPreference = useSelector((state) => state.userPreference);
  const authToken = useSelector((state) => state.auth.authToken);
  const tokenType = useSelector((state) => state.auth.tokenType);

  const [progressData, setProgressData] = React.useState(null);
  const [isXpInfoOpen, setIsXpInfoOpen] = React.useState(false);
  const [isDailyGoalModalOpen, setIsDailyGoalModalOpen] = React.useState(false);
  const [dailyGoalData, setDailyGoalData] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("engage");
  const [userGetData, setUserGetData] = React.useState(null);
  const [isProfileImageUploading, setIsProfileImageUploading] = React.useState(false);
  const [progressLoading, setProgressLoading] = React.useState(true);
  const [userMetaLoading, setUserMetaLoading] = React.useState(() =>
    Boolean(String(authToken || "").trim())
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(url_293);
        if (!cancelled && response?.data?.status === 200) {
          setProgressData(response.data.data);
        }
      } finally {
        if (!cancelled) setProgressLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!String(authToken || "").trim()) {
      setUserGetData(null);
      setUserMetaLoading(false);
      return;
    }
    let cancelled = false;
    setUserMetaLoading(true);
    const headers = { Authorization: `${tokenType} ${authToken}` };
    (async () => {
      try {
        const response = await axios.get(url_8, { headers });
        if (cancelled) return;
        if (response?.data?.status === 200 && response?.data?.data) {
          setUserGetData(response.data.data);
        } else {
          setUserGetData(null);
        }
      } catch {
        if (!cancelled) setUserGetData(null);
      } finally {
        if (!cancelled) setUserMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, tokenType]);

  const fetchDailyGoalData = React.useCallback(async () => {
    const response = await axios.get(url_294);
    if (response?.data?.status === 200) {
      setDailyGoalData(response.data.data);
      return response.data.data;
    }
    return null;
  }, []);

  const handleOpenDailyGoalModal = React.useCallback(async () => {
    try {
      if (!dailyGoalData) {
        await fetchDailyGoalData();
      }
      setIsDailyGoalModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch daily goal data:", error);
    }
  }, [dailyGoalData, fetchDailyGoalData]);

  const refreshHomepageData = React.useCallback(async () => {
    const response = await axios.get(url_293);
    if (response?.data?.status === 200) {
      setProgressData(response.data.data);
    }
  }, []);

  const subtitle = (() => {
    const pref = userPreference || {};
    const target = Array.isArray(pref.targetDetails) && pref.targetDetails[0]?.target;
    const cls = pref.classDetails?.class;
    const course = Array.isArray(pref.courseDetails) && pref.courseDetails[0]?.course;
    return [target, cls, course].filter(Boolean).join(" • ");
  })();

  const totalAttempt = progressData?.daily?.total_attempt ?? "";
  const progressPercentRaw = progressData?.daily?.progress_percent ?? "";
  const todayPoints =
    progressData?.daily?.["Points Earned Today"] ?? progressData?.daily?.xp_points;
  const xpTotal = progressData?.total_xp_point;

  const showVerifiedCheckmark = userGetData?.show_verified_tick === true;
  const profileMembershipTag = String(userGetData?.membership_tag ?? "").trim();
  const scheduleIsPaid = userGetData?.is_paid ?? userData?.is_paid ?? null;

  const isMySpaceLoading = progressLoading || userMetaLoading;

  const handleProfileImageFileSelected = React.useCallback(
    async (file) => {
      if (!file || !String(file.type || "").startsWith("image/")) {
        toastifyMessage({ status: 400, message: "Please choose an image file." });
        return;
      }
      const pref = userPreference || {};
      const targetId = pref.targetDetails?.[0]?.target_id;
      const courseId = pref.courseDetails?.[0]?.course_id;
      const classId = pref.classDetails?.class_id;
      const batchId = pref.batchDetails?.batch_id;
      if (!targetId || !courseId || !classId || !batchId) {
        toastifyMessage({
          status: 400,
          message: "Please set your course preferences in Edit Profile before changing your photo.",
        });
        return;
      }
      const u = userData || {};
      const addr = u.address || {};
      const formData = new FormData();
      formData.append("name", u.name ?? "");
      formData.append("dob", u.dob ?? "");
      formData.append("gender", u.gender ?? "");
      formData.append("roll_no", u.roll_no ?? "");
      formData.append("state", addr.state ?? "");
      formData.append("mobile_number", u.mobile ?? "");
      formData.append("city", addr.city ?? "");
      if (u.email) {
        formData.append("email", u.email);
      }
      formData.append("image", file);
      const acpsPin = Number(u.acps_pin);
      if (!Number.isNaN(acpsPin) && acpsPin >= 1000) {
        formData.append("acps_pin", String(acpsPin));
      }
      formData.append("user_target_id", targetId);
      formData.append("target_ids", JSON.stringify([courseId]));
      formData.append("class_id", classId);
      formData.append("batch_id", batchId);

      try {
        setIsProfileImageUploading(true);
        const response = await axios.post(url_6, formData);
        if (response?.data?.status === 200 && response?.data?.data) {
          const apiData = response.data.data;
          dispatch(authActions.setUser(apiData));
          dispatch(
            userPreferenceActions.setUserPreference({
              targetDetails: apiData.UsertargetDetail,
              classDetails: {
                class_id: apiData.class_id,
                class: apiData.class,
              },
              courseDetails: apiData.targets,
              batchDetails: {
                batch_id: apiData.batch_id,
                batch: apiData.batch,
              },
            })
          );
        } else {
          toastifyMessage({
            status: 400,
            message: response?.data?.message || "Could not update profile photo.",
          });
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.statusText ||
          "Could not update profile photo.";
        toastifyMessage({ status: 400, message: msg });
      } finally {
        setIsProfileImageUploading(false);
      }
    },
    [userData, userPreference, dispatch]
  );

  return (
    <main className="engage-homepage-shell my-space-shell">
      <Sidebar />
      <div className="engage-homepage my-space-page">
        {isMySpaceLoading ? (
          <div
            className="engage-homepage-page-loader"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading My Space"
          >
            <ClipLoader color="#C61C26" size={48} />
          </div>
        ) : (
          <>
            <div className="engage-homepage__fixed-top">
              <EngageHomepageHeader />
              <div className="engage-homepage__top">
                <EngageProfile
                  avatarSrc={userData?.image?.trim() || ""}
                  name={userData?.name?.trim() || ""}
                  subtitle={subtitle}
                  tierLabel={profileMembershipTag}
                  showCheckmarkBadge={showVerifiedCheckmark}
                  dailyGoalValue={String(totalAttempt).split(" ")[0] || ""}
                  dailyGoalSuffix="Solved"
                  xpValue={xpTotal !== undefined ? String(xpTotal) : ""}
                  xpSuffix="points"
                  onDailyGoalClick={handleOpenDailyGoalModal}
                  onInfoClick={() => setIsXpInfoOpen(true)}
                  onProfileImageFileSelected={handleProfileImageFileSelected}
                  isProfileImageUploading={isProfileImageUploading}
                />
              </div>
              <div className="engage-homepage__tabs-wrap" role="tablist" aria-label="My Space tabs">
                <div className="engage-homepage__tabs">
                  {/* <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "rankPredictor"}
                    className={`engage-homepage__tab ${
                      activeTab === "rankPredictor" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("rankPredictor")}
                  >
                    Rank Predictor
                  </button> */}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "engage"}
                    className={`engage-homepage__tab ${
                      activeTab === "engage" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("engage")}
                  >
                    Engage
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "progress"}
                    className={`engage-homepage__tab ${
                      activeTab === "progress" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("progress")}
                  >
                    Progress
                  </button>
                
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "bookmarks"}
                    className={`engage-homepage__tab ${
                      activeTab === "bookmarks" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("bookmarks")}
                  >
                    Bookmarks
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "mySchedule"}
                    className={`engage-homepage__tab ${
                      activeTab === "mySchedule" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("mySchedule")}
                  >
                    My Schedule
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "homework"}
                    className={`engage-homepage__tab ${
                      activeTab === "homework" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("homework")}
                  >
                    Home Work
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "notifications"}
                    className={`engage-homepage__tab ${
                      activeTab === "notifications" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("notifications")}
                  >
                    Notifications
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "referEarn"}
                    className={`engage-homepage__tab ${
                      activeTab === "referEarn" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("referEarn")}
                  >
                    Refer & Earn
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "coins"}
                    className={`engage-homepage__tab ${
                      activeTab === "coins" ? "engage-homepage__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab("coins")}
                  >
                    Coins
                  </button>
                </div>
              </div>
            </div>

            <div className="engage-homepage__scroll">
              {activeTab === "rankPredictor" && <RankPredictorHomePage />}
              {activeTab === "engage" && (
                <EngageHomepage
                  {...props}
                  totalAttempt={totalAttempt}
                  progressPercentRaw={progressPercentRaw}
                  todayPoints={todayPoints}
                  motivationMessage={progressData?.goal_footer_text ?? ""}
                  handleOpenDailyGoalModal={handleOpenDailyGoalModal}
                />
              )}
              {activeTab === "progress" && (
                <div className="my-space-progress-panel">
                  <UserAnalyticsContentComponent
                    history={history}
                    userPreference={userPreference}
                    embeddedInMySpace
                  />
                </div>
              )}
              {activeTab === "mySchedule" && (
                <div className="my-space-schedule-panel">
                  <ScheduleClass
                    history={history}
                    isPaid={scheduleIsPaid}
                    embeddedInMySpace
                  />
                </div>
              )}
              {activeTab === "homework" && (
                <div className="my-space-homework-panel">
                  <HomeWorkContent />
                </div>
              )}
              {activeTab === "bookmarks" && (
                <div className="my-space-bookmarks-panel">
                  <UserBookmarksContentComponent history={history} embeddedInMySpace />
                </div>
              )}
              {activeTab === "notifications" && (
                <div className="my-space-notifications-panel">
                  <NotificationListPanel active embeddedInMySpace heading="Notifications" />
                </div>
              )}
              {activeTab === "referEarn" && (
                <div className="my-space-refer-earn-panel">
                  <MySpaceReferAndEarn active />
                </div>
              )}
              {activeTab === "coins" && (
                <div className="my-space-coins-panel">
                  <WalletContentComponent
                    history={history}
                    userData={userData}
                    userPreference={userPreference}
                    location={location}
                    embeddedInMySpace
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <XpPointsInfoModal
        isOpen={isXpInfoOpen}
        onClose={() => setIsXpInfoOpen(false)}
        popupData={progressData?.popupdata}
      />
      <DailyGoalModal
        isOpen={isDailyGoalModalOpen}
        onClose={() => setIsDailyGoalModalOpen(false)}
        dailyGoalData={dailyGoalData}
        onSaved={async () => {
          await Promise.all([refreshHomepageData(), fetchDailyGoalData()]);
        }}
      />
    </main>
  );
}
